import React, { Component } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import { localize } from 'wanderu-i18n';
import {
    memoize,
    validationMessages as messages,
    validations,
} from 'wanderu-utils';

import baseAppConfig from 'wanderu-shellapp/src/config/baseApp.locales';

import FormInput from '../../FormInput/FormInput';
import { MoreThanOneChildError } from '../errors/errors';
import {
    isNativeInput,
    augmentChildEvent,
    childrenToArray,
} from '../utils/formUtils';

import styles from './Validate.scss';

const DEFAULT_VALIDATE_FN = () => ({ isValid: true });

class Validate extends Component {
    static propTypes = {
        className: PropTypes.string,
        inputClass: PropTypes.string,
        inputWithMessageClass: PropTypes.string,
        onValidate: PropTypes.func,
        onFocus: PropTypes.func,
        onChange: PropTypes.func,
        required: PropTypes.bool,
        value: PropTypes.any,
        type: PropTypes.oneOf(Object.keys(messages)),
        showDefaultMessage: PropTypes.bool,
        messagePlacement: PropTypes.oneOf(['bottom', 'top']),
        customValidate: PropTypes.func,
        forceShowValidation: PropTypes.bool,
    };

    static defaultProps = {
        className: '',
        inputClass: '',
        inputWithMessageClass: '',
        onValidate() {},
        onFocus() {},
        onChange() {},
        required: false,
        showDefaultMessage: false,
        messagePlacement: 'bottom',
        forceShowValidation: false,
        reactInputComponent: FormInput,
    };

    constructor(props) {
        super(props);
        const {
            customValidate,
            required,
            type,
        } = this.props;

        this._validate = this._makeValidate(customValidate, required, type);
        let { isValid, message } = this._validate(this.props.value);

        const defaultMessage = this._defaultMessage;
        if (this.props.showDefaultMessage && defaultMessage) {
            message = defaultMessage;
        }

        this.state = {
            shouldValidate: false,
            changed: false,
            value: this.props.value || '',
            isValid,
            message,
        };
    }

    UNSAFE_componentWillMount() {
        this.props.onValidate(this.state.isValid);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.forceShowValidation && !this.props.forceShowValidation) {
            this._updateValidity();
            this._updateShouldValidate(true);
        }

        if (nextProps.resetValidation && !this.props.resetValidation) {
            this._resetValidity();
        }

        if (nextProps.customValidate !== this.props.customValidate) {
            const {
                customValidate,
                required,
                type,
            } = nextProps;

            this._validate = this._makeValidate(customValidate, required, type);
            this._updateValidity();
            this._updateShouldValidate();
        }

        if (nextProps.value !== this.props.value) {
            let { isValid, message } = this._validate(nextProps.value);

            this.setState(() => ({
                isValid,
                message,
                value: nextProps.value,
            }));
        }
    }

    _makeValidate(customValidate, required, type) {
        let validate = DEFAULT_VALIDATE_FN;
        const typeValidate = validations[type];

        if (customValidate) {
            validate = memoize(customValidate);
        } else if (typeValidate) {
            validate = memoize(typeValidate);
        }

        if (required) {
            return validations.required(validate);
        }
        return validations.optional(validate);
    }

    get _defaultMessage() {
        return messages[this.props.type];
    }

    get _shouldShowDefaultMessage() {
        return this.props.showDefaultMessage;
    }

    get _shouldShowValidationMessage() {
        return !this.state.isValid && this.state.shouldValidate;
    }

    get _shouldShowMessage() {
        return this._shouldShowDefaultMessage || this._shouldShowValidationMessage;
    }

    _augumentChild() {
        const child = this._getChild();
        let newProps = {
            ...child.props,
            onBlur: augmentChildEvent(this._onInputBlur, child.props.onBlur),
            onFocus: augmentChildEvent(this._onInputFocus, child.props.onFocus),
            onChange: augmentChildEvent(this._onInputChange, child.props.onChange),
            value: child.props.value || this.state.value,
        };

        if (!isNativeInput(child)) {
            newProps.showValidity = this.state.shouldValidate;
            newProps.valid = this.state.isValid;
        }

        const { inputClass, inputWithMessageClass } = this.props;

        if (inputClass || inputWithMessageClass) {
            newProps.className = classNames(
                child.props.className,
                inputClass,
                { [inputWithMessageClass]: this._shouldShowMessage },
            );
        }

        return React.cloneElement(child, newProps);
    }

    _getChild() {
        const children = childrenToArray(this.props.children);
        this._assertOnlyOneChild(children);
        return children[0];
    }

    _assertOnlyOneChild(children) {
        if (children.length > 1) {
            throw new MoreThanOneChildError();
        }
    }

    _onInputFocus = (e) => {
        this.setState(() => ({ shouldValidate: false }));
        this.props.onFocus(e);
    }

    _onInputChange = (e) => {
        const value = e.target.value;

        this.setState(
            () => ({
                value,
                changed: true,
            }),
            () => this._updateValidity(),
        );

        this.props.onChange(e);
    }

    _onInputBlur = () => {
        this._updateValidity();
        this._updateShouldValidate();
    }

    _resetValidity() {
        this.setState(() => ({
            changed: false,
            shouldValidate: false,
        }));
    }

    _updateValidity() {
        const { userEmail } = this.props;
        const { isValid, message } = this._validate(this.state.value, undefined, userEmail);
        this.props.onValidate(isValid);

        this.setState(() => ({
            isValid,
            message,
        }));
    }

    _updateShouldValidate(forceValidate) {
        const {
            value,
            changed,
        } = this.state;

        const isOptionalWithoutValue = !this.props.required && !value;

        if (isOptionalWithoutValue) {
            return this.setState(() => ({ shouldValidate: false }));
        }

        let shouldValidate = changed || forceValidate;

        this.setState(() => ({ shouldValidate }));
    }

    _renderMessages() {
        const {
            t,
            type,
            showPasswordRequirements,
        } = this.props;
        const {
            shouldValidate,
            isValid,
            message,
        } = this.state;

        const isPasswordField = type === 'password';

        if (showPasswordRequirements && isPasswordField && !!message) {
            const passwordRequirements = messages[type];

            return (
                <ul className={`${styles.messageContainer} ${styles.passwordReqs}`}>
                    {Object.keys(passwordRequirements).map((reqType) => (
                        <li
                            className={classNames(
                                styles.passwordMessage,
                                { [styles.invalid]: !!message[`invalid-${reqType}`] },
                            )}
                            key={reqType}
                        >
                            {t(passwordRequirements[reqType])}
                        </li>
                    ))}
                </ul>
            );
        }

        if (!this._shouldShowMessage || isPasswordField) {
            return null;
        }

        return (
            <ul className={styles.messageContainer}>
                <li
                    className={classNames(
                        styles.message,
                        { [styles.invalid]: shouldValidate && !isValid },
                    )}
                    key={message}
                >
                    {t(message)}
                </li>
            </ul>
        );
    }

    render() {
        return (
            <div className={classNames(this.props.className)}>
                {this.props.messagePlacement === 'top' && this._renderMessages()}
                {this._augumentChild()}
                {this.props.messagePlacement === 'bottom' && this._renderMessages()}
            </div>
        );
    }
}

export { Validate };
export default localize(Validate, baseAppConfig.namespace);
